import { saveAs } from 'file-saver'
import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

// UI
import { NonFormUpload } from '@/components/Elements'
import { Box, Button, Flex, HStack, Heading, Spacer } from '@chakra-ui/react'
import { toast } from 'react-toastify'

//Hooks
import useUser from '@/features/users/useUser'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Constant
import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { UserStatus } from '@/features/users/constants'
import { createExcelBlob } from '@/utils/fileHelper'
import { camelToWords } from '@/utils/stringHelper'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import DateCell from '@/components/Table/components/Cell/DateCell'
import { RawTextCell } from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { LoadingSection } from '@/components/Elements'
const VendorUserListPage = () => {
  // Table State
  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:vendorUser`, {})
  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const dispatch = useDispatch()
  const { useFetchAllUsers, useUploadVendorUsers } = useUser()

  const navigate = useNavigate()
  const columnHelper = createColumnHelper()

  const uploadVendorUsers = useUploadVendorUsers()
  const { data: usersResponse, isLoading } = useFetchAllUsers({
    ...query,
    filters: { ...query?.filters, role: 'vendor' },
  })
  const { data: allUserResponse } = useFetchAllUsers({
    page: 1,
    pageSize: 0,
    sortBy: 'fullName',
    orderBy: 1,
    filters: { role: 'vendor' },
  }) // Page Size 0 means get all

  const handleUpload = async (file) => {
    if (!file) {
      toast.error('No File Uploaded')
      return
    }
    try {
      dispatch(showLoadingOverlay())
      await uploadVendorUsers.mutateAsync(file)
    } catch (error) {
      console.log(error)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleOnRowClick = ({ original }) => {
    if (original?._id) {
      navigate(`/admin/users/${original._id}?type=vendor`)
    }
  }

  const handleDownload = () => {
    if (!allUserResponse.data) return
    try {
      const excelData = allUserResponse.data.map((row) => ({
        email: row.email,
        vendorNumber: row.employeeNumber,
        firstName: row.firstName,
        lastName: row.lastName,
        contactNumber: row.contactNumber,
        supplierName: row.supplier,
        status: row.status,
      }))
      const excelBlob = createExcelBlob(excelData)
      saveAs(excelBlob, 'Vendor Users.xlsx')
    } catch (err) {
      console.log(err)
    }
  }

  const defaultColumns = useMemo(
    () => [
      columnHelper.accessor('fullName'),
      columnHelper.accessor('email', {
        cell: RawTextCell,
      }),
      columnHelper.accessor('contactNumber'),
      columnHelper.accessor('supplier'),
      columnHelper.accessor('lastLoginDate', {
        cell: DateCell,
        meta: {
          filterVariant: FilterType.DATE,
        },
      }),

      columnHelper.accessor('status', {
        meta: {
          filterVariant: FilterType.MULTI_SELECT,
          filterOptions: Object.values(UserStatus).map((status) => {
            return {
              label: camelToWords(status),
              value: status,
            }
          }),
        },
      }),
    ],
    [columnHelper]
  )

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <Flex p={5} borderBottom="1px solid #d8dbe0">
          <Heading>Vendor Users</Heading>

          <HStack mx="5">
            <Button colorScheme="gray" onClick={handleDownload}>
              Download
            </Button>
            <NonFormUpload uploadHandler={handleUpload} />
          </HStack>

          <Spacer />
          <Link to="/admin/users/create?type=vendor">
            <Button colorScheme="blue">Create User</Button>
          </Link>
        </Flex>
        <Box p={5} overflow="scroll">
          <FullTable
            data={usersResponse?.data ? usersResponse.data : []}
            rowCount={usersResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default VendorUserListPage
